import React, { FC } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Row, Table } from 'reactstrap';
import {
  ExtendedHotelReleaseGroup,
  getReleasePeriod,
  groupTypeToText,
  HotelReleaseGroupEntryList,
  HotelReservationStatus,
  ReleaseGroupType,
  stageToText,
} from '../../../../../shared/hotel';
import { TaskSummary } from '../../../../../shared/task';
import { ActionButtonModal, DateTime, LinkButton, MaterialIcon } from '../../../../components';
import { useTranslation } from '../../../../translations';
import { countBy, Fetcher, renderName, TableKVPair, useFetcher } from '../../../../utils';
import { UnverifiedEmailIcon } from '../../util/email';

export const ReleaseGroupView: FC<RouteComponentProps<{ id: string }>> = ({ match }) => {
  const fetcher = useFetcher(async () => {
    const group = await api.getHotelReleaseById(match.params.id);
    const entries = await api.getHotelReleaseGroupEntries(match.params.id);

    return {
      group,
      entries,
    };
  });

  const { ts } = useTranslation();

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <>
      {/* <LinkButton className="mb-5" color="secondary" to="/housekeeping/hotel/groups">
        Back
      </LinkButton> */}
      <h2>
        {ts('release_group')}
        {fetcher.data.group.name}
        {fetcher.data.group.displayName}
      </h2>
      <hr />
      <GroupSummary data={fetcher.data} />
      <hr />
      <ReleaseEntries entries={fetcher.data.entries} refresh={fetcher.refresh} />
    </>
  );
};

const GroupSummary: FC<{
  readonly data: { group: ExtendedHotelReleaseGroup; entries: HotelReleaseGroupEntryList };
}> = ({ data }) => {
  // provide meta keys?
  const { group, entries } = data;
  const stage = stageToText(getReleasePeriod(group, new Date()));
  const linkTask = group.createLinksTask;
  const emailTask = group.sendEmailsTask;

  const reservations = entries.entries.filter((e) => e.reservation);

  const reservationStatistics = {
    new: 0,
    pending: 0,
    cancelled: 0,
    cancelledByEvent: 0,
    confirmed: 0,
    creating: 0,
    noShow: 0,
    waitlisted: 0,
    expired: 0,
  };

  for (const reservation of reservations) {
    switch (reservation.reservation!.status) {
      case HotelReservationStatus.Cancelled: {
        reservationStatistics.cancelled++;
        break;
      }

      case HotelReservationStatus.CancelledByEvent: {
        reservationStatistics.cancelledByEvent++;
        break;
      }

      case HotelReservationStatus.Pending: {
        reservationStatistics.pending++;
        break;
      }

      case HotelReservationStatus.New: {
        reservationStatistics.new++;
        break;
      }

      case HotelReservationStatus.Confirmed: {
        reservationStatistics.confirmed++;
        break;
      }

      case HotelReservationStatus.Creating: {
        reservationStatistics.creating++;
        break;
      }

      case HotelReservationStatus.NoShow: {
        reservationStatistics.noShow++;
        break;
      }

      case HotelReservationStatus.Waitlisted: {
        reservationStatistics.waitlisted++;
        break;
      }

      case HotelReservationStatus.Expired: {
        reservationStatistics.expired++;
        break;
      }
    }
  }

  const { ts } = useTranslation();

  return (
    <Row>
      <Col xs={6}>
        <Card>
          <CardHeader>{ts('group_information')}</CardHeader>
          <CardBody>
            <Table>
              <tbody>
                <TableKVPair displayName="common.group_id" name="id" value={group.id} />
                <TableKVPair displayName="name" name="name" value={group.name} />
                <TableKVPair
                  displayName="common.display_name"
                  name="displayName"
                  value={group.displayName ?? ts('none_set')}
                />
                <TableKVPair
                  displayName="description"
                  name="description"
                  value={group.description ?? ts('none_set')}
                />
                <TableKVPair displayName="type" name="type" value={groupTypeToText(group.type)} />
                <TableKVPair
                  displayName="hotel.show_room_availability_question"
                  name="showRoomAvailability"
                  value={ts(group.showRoomAvailability ? 'common.yes' : 'common.no')}
                />
                <TableKVPair displayName="common.stage" name="stage" value={stage} />
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </Col>
      <Col xs={6}>
        {emailTask && <GroupTaskSummary task={emailTask} type="email" />}
        {linkTask && <GroupTaskSummary task={linkTask} type="link" />}
      </Col>
      <Col>
        <Card>
          <CardHeader>{ts('release_entry_statistics')}</CardHeader>
          <CardBody>
            <Table>
              <tbody>
                <TableKVPair
                  displayName="hotel.entry_amount"
                  name="count"
                  value={entries.entries.length}
                />
                <TableKVPair
                  displayName="hotel.reservation_amount"
                  name="reservations"
                  value={reservations.length}
                />
                <TableKVPair
                  displayName="hotel.cancelled_reservations"
                  name="cancelled"
                  value={reservationStatistics.cancelled}
                />
                <TableKVPair
                  displayName="hotel.event_cancelled_reservations"
                  name="cancelledByEvent"
                  value={reservationStatistics.cancelledByEvent}
                />
                <TableKVPair
                  displayName="hotel.creating_reservation"
                  name="creating"
                  value={reservationStatistics.creating}
                />
                <TableKVPair
                  displayName="hotel.confirmed_reservations"
                  name="confirmed"
                  value={reservationStatistics.confirmed}
                />
                <TableKVPair
                  displayName="hotel.new_reservations"
                  name="new"
                  value={reservationStatistics.new}
                />
                <TableKVPair
                  displayName="hotel.pending_reservations"
                  name="cancelled"
                  value={reservationStatistics.pending}
                />
                <TableKVPair
                  displayName="hotel.waitlisted_reservations"
                  name="waitlisted"
                  value={reservationStatistics.waitlisted}
                />
                <TableKVPair
                  displayName="hotel.expired_reservations"
                  name="expired"
                  value={reservationStatistics.expired}
                />
                <TableKVPair
                  displayName="hotel.no_show_reservations"
                  name="noShow"
                  value={reservationStatistics.noShow}
                />
                {group.type === ReleaseGroupType.RandomDraw && (
                  <>
                    <TableKVPair
                      displayName="hotel.entry_amount_selected"
                      name="selected"
                      value={countBy(entries.entries, (e) => !!e.isSelected)}
                    />
                    <TableKVPair
                      displayName="hotel.entry_amunt_not_selected"
                      name="notSelected"
                      value={countBy(entries.entries, (e) => !e.isSelected)}
                    />
                  </>
                )}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

const GroupTaskSummary: FC<{ readonly task: TaskSummary; readonly type: 'email' | 'link' }> = ({
  task,
  type,
}) => {
  const title = type === 'email' ? 'Email Task Summary' : 'Link Task Summary';
  return (
    <Card>
      <CardHeader>{title}</CardHeader>
      <CardBody>
        <Table>
          <TableKVPair
            displayName="common.task_id"
            name="id"
            value={<Link to={`/housekeeping/auditlog/Task/${task.id}`}>{task.id}</Link>}
          />
          <TableKVPair displayName="hotel.retry_amount" name="retryCount" value={task.retryCount} />
          <TableKVPair
            displayName="common.execution_time"
            name="runAt"
            value={<DateTime value={task.runAt} />}
          />
          <TableKVPair displayName="common.status" name="status" value={task.status} />
        </Table>
      </CardBody>
    </Card>
  );
};

const ReleaseEntries: FC<{
  readonly entries: HotelReleaseGroupEntryList;
  readonly refresh: () => void;
}> = ({ entries, refresh }) => {
  const { ts } = useTranslation();

  return (
    <>
      <h3>{ts('release_entries')}</h3>
      <Table striped>
        <thead>
          <tr>
            <th>{ts('common.user_id')}</th>
            <th>{ts('common.user')}</th>
            <th>{ts('selected')}</th>
            <th>{ts('notified')}</th>
            <th>{ts('reservation')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {entries.entries.map((entry) => (
            <tr key={entry.id}>
              <td>{entry.user.id}</td>
              <td>
                <Link to={`/housekeeping/attendees/user/${entry.user.id}`}>
                  {renderName(entry.user)}
                </Link>
              </td>
              <td>{entry.isSelected ? 'Yes' : 'No'}</td>
              <td>
                {entry.isNotified ? 'Yes' : 'No'}
                {!entry.userEmailVerified && <UnverifiedEmailIcon userId={entry.user.id} />}
              </td>
              <td>
                {entry.reservation ? (
                  <LinkButton
                    className="btn-sm"
                    color="secondary"
                    id={`viewReservation${entry.user.id}`}
                    to={`/housekeeping/hotel/user/${entry.user.id}`}
                  >
                    {ts('view_reservation')}
                  </LinkButton>
                ) : (
                  ts('no_reservation')
                )}
              </td>
              <td>
                <ActionButtonModal
                  actionContent="common.delete"
                  buttonContent={<MaterialIcon name="delete" small />}
                  className="indent"
                  color="danger"
                  id="delete"
                  onComplete={async () => {
                    await api.deleteReleaseEntry(entry.id, true);
                    refresh();
                  }}
                  title="common.delete_entry"
                >
                  {ts('are_you_sure_you_want_to_delete')}
                </ActionButtonModal>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};
